// Asset — the physical thing inspected. Property, store, vehicle, building,
// jobsite. Belongs to an Organization; optionally to an AssetGroup.
//
// `attributes` is JSONB by design: each vertical wants a different shape
// (rentals carry owner_name/commune/property_type; rent-a-car carries
// license_plate/vin/year). Engine baseline keeps it free-form; recipe-aware
// validators can refine at write time.
import { isoDateTimeSchema } from "@bitacorapro/shared";
import { z } from "zod";
import { assetGroupIdSchema, assetIdSchema, organizationIdSchema } from "../ids.js";
export const assetAttributesSchema = z.record(z.string(), z.unknown());
export const assetSchema = z.object({
    id: assetIdSchema,
    organizationId: organizationIdSchema,
    assetGroupId: assetGroupIdSchema.optional(),
    // Address is required even for non-real-estate verticals because every
    // vertical so far has wanted *some* canonical "where is this thing" string.
    // Vehicles use the operating depot address; jobsites use the site address.
    address: z.string().trim().min(1).max(500),
    unit: z.string().trim().max(50).optional(),
    notes: z.string().trim().max(5000).optional(),
    attributes: assetAttributesSchema,
    createdAt: isoDateTimeSchema,
});
export const assetCreateSchema = assetSchema.omit({ id: true, createdAt: true }).extend({
    id: assetIdSchema.optional(),
    attributes: assetAttributesSchema.default({}),
});
export const assetUpdateSchema = assetSchema
    .pick({
    assetGroupId: true,
    address: true,
    unit: true,
    notes: true,
    attributes: true,
})
    .partial()
    .extend({
    // Clearing the group is a valid update for recipes that allow
    // ungrouped assets. The base assetSchema models assetGroupId as
    // optional-only (uuid | undefined); widen to nullable here so an
    // explicit `null` reaches the route's rootEntity guard, which
    // rejects it for group-rooted recipes with
    // RECIPE_FORBIDS_ASSET_WITHOUT_GROUP rather than a generic
    // VALIDATION_ERROR.
    assetGroupId: assetGroupIdSchema.nullable().optional(),
});
// The geo blob persisted at `assets.attributes.geo` — camelCase, matching
// the PickedPlace shape the address-autocomplete write path
// (apps/web-svelte/src/lib/util/google-maps.ts) persists verbatim.
// Mirrors reportGeoSchema's role for the reports table: one canonical
// shape both writers and readers import instead of each side hand-rolling
// its own narrowing and silently drifting — which is exactly what
// happened here once: an earlier write path re-keyed to snake_case
// (place_id / formatted_address), the reader never matched it, and the
// address text silently dropped to "" on every asset created in between,
// while lat/lng kept working because those two key names never diverged.
export const assetGeoSchema = z.object({
    placeId: z.string().max(500),
    formattedAddress: z.string().max(500),
    lat: z.number(),
    lng: z.number(),
    // Present only when this geo did NOT come from a live user place-pick
    // — e.g. a forward-geocoded coordinate from a backfill. Absent means
    // "a person picked this address in an active session", the strongest
    // provenance this app has for an asset's location. A surface that
    // renders this pin adjacent to a signed acta should keep preferring a
    // report's signedGeo over an assetGeo whose source is set.
    source: z.string().max(80).optional(),
});
// Narrow an asset's untyped `attributes.geo` at the read boundary.
// Tolerates the legacy snake_case shape (place_id / formatted_address)
// an earlier writer persisted before the PickedPlace-verbatim fix — see
// the comment above assetGeoSchema. Returns null for anything that
// doesn't resolve to at least a valid lat/lng pair.
export function parseAssetGeo(attributes) {
    const raw = attributes?.["geo"];
    if (!raw || typeof raw !== "object")
        return null;
    const g = raw;
    const lat = g["lat"];
    const lng = g["lng"];
    if (typeof lat !== "number" || typeof lng !== "number")
        return null;
    const formattedAddress = typeof g["formattedAddress"] === "string"
        ? g["formattedAddress"]
        : typeof g["formatted_address"] === "string"
            ? g["formatted_address"]
            : "";
    const placeId = typeof g["placeId"] === "string"
        ? g["placeId"]
        : typeof g["place_id"] === "string"
            ? g["place_id"]
            : "";
    const source = typeof g["source"] === "string" ? g["source"] : undefined;
    const parsed = assetGeoSchema.safeParse({
        placeId,
        formattedAddress,
        lat,
        lng,
        source,
    });
    return parsed.success ? parsed.data : null;
}
/**
 * La clave del atributo que lleva el estado de la unidad dentro de
 * `assets.attributes`. Vive acá, y no repetida como literal en cada
 * consumidor, porque desde ADR-0065 (enmendado 2026-09-12) es el dato
 * que gobierna si una unidad puede recibir observaciones
 * (`observationsRequireAssetStatusIn`): un typo en un solo call site
 * abriría o cerraría el gate en silencio.
 */
export const ASSET_STATUS_ATTRIBUTE_KEY = "asset_status";
/**
 * Lee el estado de la unidad desde su blob de atributos. Devuelve null
 * cuando el atributo no existe o no es string — que es el caso de toda
 * unidad que nunca pasó por activación, y el que
 * `assertObservationEntryAllowed` trata como fail-closed.
 *
 * No valida contra `recipe.assetStatuses`: la pertenencia al vocabulario
 * se verifica en la escritura del asset, no en cada lectura.
 */
export function readAssetStatus(attributes) {
    if (attributes === null || typeof attributes !== "object")
        return null;
    const raw = attributes[ASSET_STATUS_ATTRIBUTE_KEY];
    return typeof raw === "string" && raw.length > 0 ? raw : null;
}
//# sourceMappingURL=asset.js.map